import { useDisclosure } from '@chakra-ui/react'
import type React from 'react'
import { useSelector } from 'react-redux'
import AddCircleIcon from '../../../../../assets/icons/add-circle-stroke-rounded'
import InfoBox from '../../../../../components/InfoBox'
import PageWrapper from '../../../../../components/Pagewrapper'
import Table from '../../../../../components/Table/Table'
import { useGetTeamMembersQuery } from '../../../../../redux/services/coreApi'
import { setTeamTableState } from '../../../../../redux/slices/tableFiltersSlice'
import type { RootState } from '../../../../../redux/store'
import type { Role } from '../../../../../types/coreApi-types'
import AddTeamMemberModal from './components/AddTeamMemberModal'

// Friendly labels for the back-office roles shown in the roster.
const ROLE_LABELS: Partial<Record<Role, string>> = {
  Admin: 'Admin',
  ComplianceOfficer: 'Compliance Officer',
  FinanceOfficer: 'Finance Officer',
  OperationsOfficer: 'Operations Officer',
  Bookkeeper: 'Bookkeeper',
}

const TeamScreen: React.FC = () => {
  const { data: teamMembers, isLoading } = useGetTeamMembersQuery()
  const addTeamMemberModal = useDisclosure()

  const teamTableState = useSelector((state: RootState) => state.teamTable)

  return (
    <PageWrapper title="Team">
      <Table
        tableState={teamTableState}
        setTableState={setTeamTableState}
        navigate={() => null}
        isLoading={isLoading}
        onClickRow={() => null}
        emptyDataRender={
          <div className="text-xl mb-3">No team members found</div>
        }
        defaultData={
          teamMembers?.map(member => ({
            ...member,
            name:
              [member.firstName, member.lastName].filter(Boolean).join(' ') ||
              '—',
            roleLabel: ROLE_LABELS[member.role] ?? member.role,
            status: member.hasLogin ? 'Active' : 'Pending',
          })) ?? []
        }
        schema={[
          {
            cardTitle: true,
            header: 'Name',
            accessorKey: 'name',
            type: 'string',
            priority: 1,
            newlyAdded: 'createdAt',
          },
          {
            header: 'Email',
            accessorKey: 'email',
            type: 'string',
            priority: 2,
          },
          {
            header: 'Role',
            accessorKey: 'roleLabel',
            type: 'string',
            priority: 3,
          },
          {
            header: 'Status',
            accessorKey: 'status',
            type: 'status',
            priority: 4,
            cellValueWrapper: value => (
              <div className="w-min">
                <InfoBox colorScheme={value === 'Active' ? 'green' : 'orange'}>
                  {value}
                </InfoBox>
              </div>
            ),
          },
          {
            header: 'Date Added',
            accessorKey: 'createdAt',
            type: 'date',
            priority: 5,
          },
        ]}
      />

      <div className="flex justify-end mt-6">
        <button
          className="button flex items-center gap-2 bg-primary text-white hover:bg-primary-dark transition-colors"
          onClick={addTeamMemberModal.onOpen}
        >
          <AddCircleIcon width={20} height={20} color="white" />
          <span>Invite Team Member</span>
        </button>
      </div>

      <AddTeamMemberModal
        isOpen={addTeamMemberModal.isOpen}
        onClose={addTeamMemberModal.onClose}
      />
    </PageWrapper>
  )
}

export default TeamScreen
